import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/projects`;

/**
 * One service's output sample — the pruned JSON shape of its parsed output, captured from the
 * project's latest successful run (mirrors ServiceOutputSampleDto). `truncated` means the sampler's
 * caps cut shape (not just value previews) — the picker warns rather than presenting a partial
 * tree as complete.
 */
export interface ServiceOutputSample {
  sampleJson: string;
  wireType: string;
  capturedAt: string;
  truncated: boolean;
  /** Sampled response headers (names verbatim, sensitive values masked server-side); absent for services with no HTTP exchange. */
  headers?: Record<string, string> | null;
  statusCode?: number | null;
}

/**
 * Per-service output samples for a (project, workflow) — feeds the BBEL editor's scope tree so
 * `services.<id>.output` shows the real response shape (#5683). Empty until the first successful
 * run under this project. Keyed reactively so org/project/workflow switches refetch.
 */
export function useWorkflowOutputSamples(projectId: Ref<string>, workflowId: Ref<string>) {
  const { data, error, refetch } = useQuery({
    queryKey: computed(() => ["output-samples", projectId.value, workflowId.value]),
    queryFn: () =>
      fetchWrapper.get<Record<string, ServiceOutputSample>>(
        `${BASE}/${projectId.value}/workflows/${workflowId.value}/output-samples`,
      ),
    enabled: computed(() => !!projectId.value && !!workflowId.value),
  });

  return {
    outputSamples: computed(() => data.value ?? {}),
    outputSamplesError: error as Ref<ApiError | null>,
    refetchOutputSamples: refetch,
  };
}
